import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import SearchableSelect from '../../components/SearchableSelect';
import mcpService, { Mcp, MCP_TIPOS } from '../../services/mcpService';

interface GrupoMcpMediosPagoTabProps {
  grupoMcp: number;
}

const medioKey = (medio: Mcp) => `${medio.tipo}::${medio.mcp}`;

const getTipoLabel = (tipo: string) => MCP_TIPOS.find((item) => item.value === tipo)?.label || tipo;

const getMedioLabel = (medio: Mcp) => {
  const descripcion = medio.descripcion?.trim();
  return descripcion ? `${medio.mcp} - ${descripcion}` : medio.mcp;
};

const GrupoMcpMediosPagoTab: React.FC<GrupoMcpMediosPagoTabProps> = ({ grupoMcp }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [medios, setMedios] = useState<Mcp[]>([]);
  const [loadingMedios, setLoadingMedios] = useState(false);
  const [medioSeleccionado, setMedioSeleccionado] = useState('');

  const loadMedios = useCallback(async () => {
    try {
      setLoadingMedios(true);
      const data = await mcpService.getAllMcps();
      setMedios(
        data
          .filter((medio) => Boolean(medio.mcp?.trim()) && Boolean(medio.tipo?.trim()))
          .sort((a, b) => {
            const tipoCompare = (a.tipo || '').localeCompare(b.tipo || '');
            if (tipoCompare !== 0) return tipoCompare;
            return (a.mcp || '').localeCompare(b.mcp || '');
          }),
      );
    } catch (error) {
      console.error('Error al cargar medios de pago:', error);
      enqueueSnackbar('Error al cargar la lista de medios de pago', { variant: 'error' });
      setMedios([]);
    } finally {
      setLoadingMedios(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadMedios();
  }, [loadMedios]);

  const mediosAsignados = useMemo(
    () => medios.filter((medio) => medio.grupo === grupoMcp),
    [medios, grupoMcp],
  );

  const mediosDisponibles = useMemo(
    () => medios.filter((medio) => medio.grupo == null),
    [medios],
  );

  const mediosDisponiblesOptions = useMemo(
    () =>
      mediosDisponibles.map((medio) => ({
        value: medioKey(medio),
        label: `${getMedioLabel(medio)} (${getTipoLabel(medio.tipo)})`,
      })),
    [mediosDisponibles],
  );

  const handleAgregarMedio = async () => {
    const medio = mediosDisponibles.find((item) => medioKey(item) === medioSeleccionado);
    if (!medio) {
      enqueueSnackbar('Debe seleccionar un medio de pago', { variant: 'warning' });
      return;
    }

    try {
      await mcpService.updateMcpGrupo(medio, grupoMcp);
      enqueueSnackbar('Medio de pago asignado exitosamente', { variant: 'success' });
      setMedioSeleccionado('');
      loadMedios();
    } catch (error: any) {
      console.error('Error al asignar medio de pago:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al asignar medio de pago', { variant: 'error' });
    }
  };

  const handleEliminarMedio = async (medio: Mcp) => {
    if (!window.confirm(`¿Está seguro de eliminar el medio de pago ${medio.mcp} de este grupo?`)) {
      return;
    }

    try {
      await mcpService.updateMcpGrupo(medio, null);
      enqueueSnackbar('Medio de pago eliminado exitosamente', { variant: 'success' });
      loadMedios();
    } catch (error: any) {
      console.error('Error al eliminar medio de pago:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar medio de pago', { variant: 'error' });
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#1976d2' }}>
        Medios de Pago Habilitados al Grupo de Caja
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Seleccione medios de pago sin grupo asignado para habilitarlos en este grupo de caja.
      </Typography>

      <Card
        sx={{
          mb: 3,
          p: 2,
          bgcolor: '#fafafa',
          borderRadius: 2,
          border: '1px solid #e0e0e0',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <SearchableSelect
              label="Seleccionar Medio de Pago"
              value={medioSeleccionado || null}
              onChange={(val) => setMedioSeleccionado(val ?? '')}
              options={mediosDisponiblesOptions}
              loading={loadingMedios}
              allowEmpty
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#ffffff' } }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAgregarMedio}
              disabled={!medioSeleccionado || loadingMedios}
              fullWidth
              sx={{
                borderRadius: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                },
              }}
            >
              Agregar Medio de Pago
            </Button>
          </Grid>
        </Grid>
      </Card>

      {mediosAsignados.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No hay medios de pago asignados a este grupo de caja
        </Typography>
      ) : (
        <Card sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <List>
            {mediosAsignados.map((medio) => (
              <ListItem key={medioKey(medio)} divider>
                <ListItemText
                  primary={getMedioLabel(medio)}
                  secondary={`${getTipoLabel(medio.tipo)}${medio.clase ? ` - ${medio.clase}` : ''}`}
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Eliminar medio de pago">
                    <IconButton
                      edge="end"
                      aria-label="eliminar"
                      onClick={() => handleEliminarMedio(medio)}
                      sx={{
                        bgcolor: '#f44336',
                        color: '#fff',
                        borderRadius: 1.5,
                        '&:hover': {
                          bgcolor: '#d32f2f',
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s',
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Card>
      )}
    </Box>
  );
};

export default GrupoMcpMediosPagoTab;
